import { useState, useEffect } from 'react';
import { db } from '../../config/firebase';
import { formatDateTime, resolveUserName } from '../../utils/formatters';

const ACTION_LABELS = {
  pif_submitted: 'PIF Submitted',
  analyst_assigned: 'Analyst Assigned',
  analyst_review_completed: 'Analyst Review Completed',
  peer_reviewer_assigned: 'Peer Reviewer Assigned',
  peer_review_completed: 'Peer Review Completed',
  hop_review_completed: 'HoP Review Completed',
  deal_sent_back: 'Deal Sent Back to Analyst',
  iic_approval_requested: 'IIC Approval Requested',
  iic_decision_recorded: 'IIC Decision Recorded',
  iic_approval_complete: 'IIC Approval Complete',
  deal_denied: 'Deal Denied',
  deal_suspended: 'Deal Suspended',
  deal_reactivated: 'Deal Reactivated',
  company_notified: 'Company Notified',
  ai_summary_confirmed: 'AI Summary Confirmed',
  pif_reopened: 'PIF Reopened',
  pif_resubmitted: 'PIF Resubmitted',
};

// Resolves UIDs to names for legacy entries that pre-date performed_by_name.
// Caches per panel instance so the same UID is not re-fetched within a session.
function AuditEntry({ entry }) {
  const [resolvedName, setResolvedName] = useState(null);

  const displayName = entry.performed_by_name ||
    (entry.performed_by === 'system' ? 'System' : null);

  useEffect(() => {
    if (displayName || !entry.performed_by || entry.performed_by === 'system') return;
    resolveUserName(entry.performed_by, db).then(setResolvedName);
  }, [entry.performed_by, displayName]);

  const byLabel = displayName || resolvedName ||
    (resolvedName === null && entry.performed_by
      ? <span style={{ color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>Loading…</span>
      : 'Unknown User');

  return (
    <div style={{ display: 'flex', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--color-border)' }}>
      <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--color-accent)', flexShrink: 0, marginTop: 5 }} />
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <p style={{ fontSize: 14, fontWeight: 500 }}>
            {ACTION_LABELS[entry.action] || entry.action}
          </p>
          <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', whiteSpace: 'nowrap', marginLeft: 8 }}>
            {formatDateTime(entry.performed_at)}
          </p>
        </div>
        <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 2 }}>
          By: {byLabel}
        </p>
        {entry.details && Object.keys(entry.details).length > 0 && (
          <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 2 }}>
            {Object.entries(entry.details)
              .filter(([, v]) => v !== null && v !== undefined && v !== '')
              .map(([k, v]) => `${k}: ${v}`)
              .join(' · ')
            }
          </p>
        )}
      </div>
    </div>
  );
}

export default function AuditLogPanel({ entries }) {
  if (!entries?.length) {
    return <p className="text-muted" style={{ fontSize: 13 }}>No audit log entries yet.</p>;
  }
  return (
    <div>
      {entries.map(entry => <AuditEntry key={entry.id} entry={entry} />)}
    </div>
  );
}
